import { useWidth } from '../hooks';
import type { CalBin } from '../types';

export interface LinePoint { label: string; value: number | null; n?: number }

/** 꺾은선. n 이 minN 미만인 점은 찍지 않고 선을 끊는다 (적은 표본으로 추세를 말하지 않기). */
export function LineChart({ points, min = 0, max = 1, target, format, height = 150, minN = 1, ariaLabel }: {
  points: LinePoint[]; min?: number; max?: number; target?: number; format: (v: number) => string;
  height?: number; minN?: number; ariaLabel: string;
}) {
  const [ref, width] = useWidth<HTMLDivElement>();
  const pad = { l: 40, r: 10, t: 10, b: 22 };
  const iw = Math.max(0, width - pad.l - pad.r);
  const ih = height - pad.t - pad.b;
  const x = (i: number) => pad.l + (points.length <= 1 ? iw / 2 : (i * iw) / (points.length - 1));
  const y = (v: number) => pad.t + ih - ((Math.min(max, Math.max(min, v)) - min) / (max - min || 1)) * ih;
  const shown = points.map((p) => (p.value != null && (p.n ?? minN) >= minN ? p.value : null));
  const segments: string[] = [];
  let current: string[] = [];
  shown.forEach((v, i) => {
    if (v != null) current.push(`${x(i).toFixed(1)},${y(v).toFixed(1)}`);
    else if (current.length) {
      segments.push(current.join(' '));
      current = [];
    }
  });
  if (current.length) segments.push(current.join(' '));
  const labels = points.length > 2 ? [0, Math.floor((points.length - 1) / 2), points.length - 1] : points.map((_, i) => i);
  return (
    <div ref={ref} className="chart" style={{ height }}>
      {width > 0 && (
        <svg width={width} height={height} role="img" aria-label={ariaLabel}>
          {[min, (min + max) / 2, max].map((t) => (
            <g key={t}>
              <line className="grid" x1={pad.l} x2={width - pad.r} y1={y(t)} y2={y(t)} />
              <text className="axis" x={pad.l - 6} y={y(t) + 4} textAnchor="end">{format(t)}</text>
            </g>
          ))}
          {target != null && <line className="target" x1={pad.l} x2={width - pad.r} y1={y(target)} y2={y(target)} />}
          {segments.map((s, i) => <polyline key={i} className="line" points={s} />)}
          {shown.map((v, i) =>
            v == null ? null : (
              <circle key={i} className="dot" cx={x(i)} cy={y(v)} r={3.5}>
                <title>{`${points[i].label}: ${format(v)}${points[i].n != null ? ` (n=${points[i].n})` : ''}`}</title>
              </circle>
            ),
          )}
          {labels.map((i) => (
            <text key={i} className="axis" x={x(i)} y={height - 6} textAnchor={i === 0 ? 'start' : i === points.length - 1 ? 'end' : 'middle'}>
              {points[i]?.label}
            </text>
          ))}
        </svg>
      )}
    </div>
  );
}

/** 막대. sub 는 막대 아래쪽에 진하게 겹쳐 그린다 (예: '다시' 수). */
export function Bars({ data, height = 110, ariaLabel }: { data: { label: string; value: number; sub?: number }[]; height?: number; ariaLabel: string }) {
  const [ref, width] = useWidth<HTMLDivElement>();
  const pad = { l: 4, r: 4, t: 8, b: 20 };
  const ih = height - pad.t - pad.b;
  const top = Math.max(1, ...data.map((d) => d.value));
  const bw = data.length ? (width - pad.l - pad.r) / data.length : 0;
  const edges = data.length > 1 ? [0, data.length - 1] : data.length ? [0] : [];
  return (
    <div ref={ref} className="chart" style={{ height }}>
      {width > 0 && (
        <svg width={width} height={height} role="img" aria-label={ariaLabel}>
          <line className="grid" x1={pad.l} x2={width - pad.r} y1={pad.t + ih} y2={pad.t + ih} />
          {data.map((d, i) => {
            const h = (d.value / top) * ih;
            const sh = ((d.sub ?? 0) / top) * ih;
            const x0 = pad.l + i * bw + bw * 0.15;
            return (
              <g key={i}>
                <title>{`${d.label}: ${d.value}${d.sub ? ` (다시 ${d.sub})` : ''}`}</title>
                {h > 0 && <rect className="bar-rect" x={x0} y={pad.t + ih - h} width={bw * 0.7} height={h} rx={2} />}
                {sh > 0 && <rect className="bar-sub" x={x0} y={pad.t + ih - sh} width={bw * 0.7} height={sh} rx={2} />}
              </g>
            );
          })}
          {edges.map((i) => (
            <text key={i} className="axis" x={pad.l + i * bw + bw / 2} y={height - 5} textAnchor={i === 0 ? 'start' : 'end'}>
              {data[i].label}
            </text>
          ))}
        </svg>
      )}
    </div>
  );
}

/** 보정 곡선: x = 말한 확률, y = 실제로 일어난 비율. 대각선에 붙을수록 잘 보정됨. */
export function CalibrationChart({ bins, ariaLabel }: { bins: CalBin[]; ariaLabel: string }) {
  const [ref, width] = useWidth<HTMLDivElement>();
  const size = Math.min(width, 340);
  const pad = 36;
  const inner = Math.max(0, size - pad - 12);
  const sx = (p: number) => pad + p * inner;
  const sy = (p: number) => 12 + inner - p * inner;
  const top = Math.max(1, ...bins.map((b) => b.n));
  const pts = bins.flatMap((b) => (b.n > 0 && b.mean_p != null && b.freq != null ? [{ ...b, p: b.mean_p, f: b.freq }] : []));
  return (
    <div ref={ref} className="chart" style={{ height: size > 0 ? size : 300 }}>
      {size > 0 && (
        <svg width={size} height={size} role="img" aria-label={ariaLabel}>
          {[0, 0.5, 1].map((t) => (
            <g key={t}>
              <line className="grid" x1={sx(0)} x2={sx(1)} y1={sy(t)} y2={sy(t)} />
              <text className="axis" x={pad - 6} y={sy(t) + 4} textAnchor="end">{`${t * 100}%`}</text>
              <text className="axis" x={sx(t)} y={size - 2} textAnchor={t === 1 ? 'end' : t === 0 ? 'start' : 'middle'}>{`${t * 100}%`}</text>
            </g>
          ))}
          <line className="diag" x1={sx(0)} y1={sy(0)} x2={sx(1)} y2={sy(1)} />
          {pts.map((b) => (
            <g key={b.lo} className={b.n < 3 ? 'faint' : ''}>
              {b.ci_lo != null && b.ci_hi != null && <line className="ci" x1={sx(b.p)} x2={sx(b.p)} y1={sy(b.ci_lo)} y2={sy(b.ci_hi)} />}
              <circle className="dot" cx={sx(b.p)} cy={sy(b.f)} r={3 + 6 * Math.sqrt(b.n / top)}>
                <title>{`말한 확률 평균 ${Math.round(b.p * 100)}% → 실제 ${Math.round(b.f * 100)}% (n=${b.n})`}</title>
              </circle>
            </g>
          ))}
        </svg>
      )}
    </div>
  );
}

export function StackBar({ parts }: { parts: { label: string; value: number; tone: string }[] }) {
  const total = parts.reduce((s, p) => s + p.value, 0);
  return (
    <div>
      <div className="stack" aria-hidden="true">
        {total > 0 && parts.map((p) => (p.value > 0 ? <span key={p.label} className={`stack-seg ${p.tone}`} style={{ flexGrow: p.value }} /> : null))}
      </div>
      <ul className="legend">
        {parts.map((p) => (
          <li key={p.label}>
            <span className={`swatch ${p.tone}`} />
            {p.label} <b>{p.value}</b>
          </li>
        ))}
      </ul>
    </div>
  );
}
